import { useMutation } from "@tanstack/vue-query";
import { useNotifications, type NotificationConfig } from "@/api/composables/useNotifications";
import { useTracking } from "@/api/composables/useTracking";
import type { TrackingEventConfig } from "@/api/config/tracking";
import type { RudderstackEventNames } from "@/types/rudderstack";

export interface EnhancedMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
  TEventName extends RudderstackEventNames = RudderstackEventNames,
> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  onMutate?: (variables: TVariables) => Promise<TContext | undefined> | TContext | undefined;
  mutationKey?: string[];
  notifications?: NotificationConfig;
  tracking?:
    | TrackingEventConfig<TEventName>
    | {
        event: (variables: TVariables) => TEventName;
        properties?: (variables: TVariables) => Record<string, any>;
      };
  retry?: number;
}

export function useEnhancedMutation<
  TData,
  TError,
  TVariables,
  TContext,
  TEventName extends RudderstackEventNames = RudderstackEventNames,
>(options: EnhancedMutationOptions<TData, TError, TVariables, TContext, TEventName>) {
  const { notifications, tracking, retry, ...mutationOptions } = options;
  const { showSuccess, showError, showLoading, clearNotification } = useNotifications();
  const { trackSuccess, trackError } = useTracking();

  let loadingToastId: string | undefined;

  return useMutation({
    ...mutationOptions,
    onMutate: async (variables) => {
      // Show loading notification if configured
      if (notifications?.loading) {
        loadingToastId = showLoading(notifications.loading.message);
      }

      // Call original onMutate if provided
      if (options.onMutate) {
        return await options.onMutate(variables);
      }
    },
    onSuccess: async (data, variables, context) => {
      // Clear loading notification
      if (loadingToastId) {
        clearNotification(loadingToastId);
        loadingToastId = undefined;
      }

      // Show success notification if configured
      if (notifications?.success) {
        showSuccess(notifications.success.message);
      }

      // Track success event if configured
      if (tracking) {
        const properties = tracking.properties ? tracking.properties(variables) : {};
        const eventName = typeof tracking.event === "function" ? tracking.event(variables) : tracking.event;
        trackSuccess(eventName, properties);
      }

      // Call original onSuccess if provided
      if (options.onSuccess) {
        await options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Clear loading notification
      if (loadingToastId) {
        clearNotification(loadingToastId);
        loadingToastId = undefined;
      }

      // Show error notification if configured
      if (notifications?.error) {
        showError(notifications.error.message.length ? notifications.error.message : error.message);
      }

      // Track error event if configured
      if (tracking) {
        const properties = tracking.properties ? tracking.properties(variables) : {};
        const eventName = typeof tracking.event === "function" ? tracking.event(variables) : tracking.event;
        trackError(eventName, properties);
      }

      // Call original onError if provided
      if (options.onError) {
        options.onError(error as TError, variables, context);
      }
    },
    retry: retry ?? 1,
  });
}
